"use client";

import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";
import Image from "next/image";
import IconSprite from "../common/IconSprite";

type Props = {
  onOpenFilters?: () => void;
  baseSearchPath?: string;
};

export default function Search({ onOpenFilters, baseSearchPath }: Props) {
  const searchParams = useSearchParams();
  const pathName = usePathname();
  const { replace } = useRouter();
  const [inputValue, setInputValue] = useState(searchParams.get("query") || "");
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSearch = useDebouncedCallback((term: string) => {
    const params = new URLSearchParams(searchParams);
    if (term) {
      params.set("query", term);
    } else {
      params.delete("query");
    }

    const targetPath = baseSearchPath ? baseSearchPath : pathName;
    replace(`${targetPath}?${params.toString()}`, { scroll: false });
  }, 600);

  const inputWidthClass = isMobile && onOpenFilters ? "w-[90%]" : "w-full";

  return (
    <div className="flex items-center bg-white px-4 pt-6 pb-2 mb-2 lg:mb-0">
      <div className={`relative ${inputWidthClass}`}>
        <label htmlFor="search" className="sr-only">
          Search
        </label>
        <input
          id="search"
          className="block w-full border border-gray02 bg-light01 py-3 pl-3 pr-10 text-md outline-none placeholder:text-dark02"
          placeholder="Buscar"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            handleSearch(e.target.value);
          }}
        />
        <button
          onClick={() => {
            if (inputValue) {
              setInputValue('');
              handleSearch('');
            }
          }}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
          aria-label={inputValue ? "Clear search" : "Search"}
        >
          <IconSprite
            id={inputValue ? "ic_close" : "ic_search"}
            className={`${inputValue ? 'h-[14px] w-[14px]' : 'h-[18px] w-[18px]'} text-gray-500`}
          />
        </button>
      </div>
      {isMobile && onOpenFilters && (
        <button
          onClick={onOpenFilters}
          className="ml-2 p-2 bg-[#CCCCCC] text-white rounded h-full"
        >
          <Image
            src="/assets/icons/filter.svg"
            alt="Filtros"
            width={20}
            height={20}
            className="h-5 w-5"
          />
        </button>
      )}
    </div>
  );
}
